import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./Table";
import { HumanResources, HRStatus } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { useLogger } from '@/contexts/LogContext';
import { LogLevel, LogCategory } from '@/types/logs';
import { Loader2, Search } from 'lucide-react';
import { API_BASE_URL } from '@/config';

const StatusPill: React.FC<{ status: HRStatus }> = ({ status }) => (
    <span
        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
            status === HRStatus.ACTIVE
                ? 'bg-green-500/20 text-green-300'
                : status === HRStatus.ON_LEAVE
                ? 'bg-yellow-500/20 text-yellow-300'
                : 'bg-red-500/20 text-red-300' // INACTIVE
        }`}
    >
        {status}
    </span>
);

const ActionButton: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
    <button 
        onClick={onClick}
        className="bg-kala-secondary text-kala-900 px-3 py-1 rounded-md font-bold hover:bg-cyan-400 transition-colors text-xs"
    >
        {children}
    </button>
);

const HRDRealDataTable = () => {
  const [staff, setStaff] = useState<HumanResources[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const { notify } = useToast();
  const { addLog } = useLogger();
  const PAGE_SIZE = 10;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    addLog("Fetching HR data.", LogLevel.INFO, LogCategory.DATA);

    try {
        const response = await fetch(`${API_BASE_URL}/api/human-resources`);
        if (response.ok) {
            const data = await response.json();
            setStaff(data);
            addLog("Successfully fetched HR data from API.", LogLevel.SUCCESS, LogCategory.API);
        } else {
            throw new Error('API fetch failed, attempting fallback.');
        }
    } catch (apiError: unknown) {
        addLog((apiError as Error).message, LogLevel.WARN, LogCategory.API);
        try {
            const fallbackResponse = await fetch('/human-resources.json');
            if (fallbackResponse.ok) {
                const data = await fallbackResponse.json();
                setStaff(data);
                notify('Could not reach server. Displaying local data.', 'warn');
            } else {
                throw new Error('Fallback data not available.');
            }
        } catch (fallbackError: unknown) {
            const finalError = `Critical: Failed to load any HR data: ${(fallbackError as Error).message}`;
            addLog(finalError, LogLevel.CRITICAL, LogCategory.DATA);
            setError(finalError);
            notify(finalError, 'error');
        }
    } finally {
        setIsLoading(false);
    }
  }, [addLog, notify]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = (staffMember: HumanResources) => {
    addLog(`Viewing details for staff: ${staffMember.name}`, LogLevel.INFO, LogCategory.GENERAL);
    notify(`Displaying details for ${staffMember.name}.`, 'info');
  };

  const filteredStaff = useMemo(() => {
    return staff.filter(member =>
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.status.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [staff, searchTerm]);

  const displayedStaff = useMemo(() => {
    return filteredStaff.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [filteredStaff, page]);

  const hasMore = page * PAGE_SIZE < filteredStaff.length;

  if (isLoading && staff.length === 0) {
    return (
        <div className="flex justify-center items-center h-48">
            <Loader2 className="w-8 h-8 text-kala-secondary animate-spin" />
            <p className="ml-4 text-kala-400">Loading HR records...</p>
        </div>
    );
  }

  return (
    <div className="bg-kala-900 text-white rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Human Resources</h2>
             <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
                <input
                    type="text"
                    placeholder="Search by name, role, or status..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="bg-kala-800 border border-kala-700 rounded-full pl-10 pr-4 py-2 text-sm text-white focus:ring-1 focus:ring-kala-secondary outline-none w-64"
                />
            </div>
        </div>
        
        {error && <div className="p-4 mb-4 bg-red-500/10 text-red-400 text-center text-sm rounded-lg">{error}</div>}

      <Table>
        <TableCaption>A list of all staff members.</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Join Date</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayedStaff.map((member) => (
            <TableRow key={member.id}>
              <TableCell className="font-medium">{member.name}</TableCell>
              <TableCell>{member.role}</TableCell>
              <TableCell>{new Date(member.joinDate).toLocaleDateString()}</TableCell>
              <TableCell><StatusPill status={member.status} /></TableCell>
              <TableCell className="text-right">
                <ActionButton onClick={() => handleAction(member)}>View</ActionButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

        {displayedStaff.length === 0 && !isLoading && (
            <div className="text-center p-8 text-kala-500">
                <p>No staff members found.</p>
            </div>
        )}

      <div className="flex justify-between items-center mt-6">
        <button 
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || isLoading}
            className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
            Previous
        </button>
        <span className="text-kala-400">Page {page} of {Math.ceil(filteredStaff.length / PAGE_SIZE)}</span>
        <button 
            onClick={() => setPage(p => p + 1)}
            disabled={!hasMore || isLoading}
            className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
            Next
        </button>
      </div>
    </div>
  );
};

export default HRDRealDataTable;
